import React, { useState, useEffect, useRef } from "react";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Button, Space, Typography, Dropdown, Modal, Alert, Badge, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, RobotOutlined, CopyOutlined, DeleteOutlined, EllipsisOutlined, EyeOutlined } from "@ant-design/icons";
import { format } from "date-fns";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { SupplementRegimen, JobStatus } from "../../../../types/healthcare/SupplementTypes";
import { listPatientRegimens, queueSupplementJob, getSupplementJobStatus, archiveRegimen, duplicateRegimen, cancelSupplementJob } from "../../../../api/healthcare/SupplementApi";
import { getSupplementCatalog } from "../../../../api/inventory/productApi";
import type { Product } from "../../../../types/inventory/ProductTypes";
import { fetchClinicCompany } from "../../../../api/admin/companyApi";
import { useNotifications } from "../../../../context/NotificationContext";
import SupplementQuestionnaire, { SupplementQuestionnaireAnswers } from "./supplements/SupplementQuestionnaire";
import RegimenEditorDrawer from "./supplements/RegimenEditorDrawer";
import { User } from "../../../../types/admin/UserTypes";
import SupplementRegimenModal from "../../../patient/components/SupplementRegimenModal";
import DataTable from "../../../../components/shared/DataTable";
import InlineLoading from "../../../../components/shared/InlineLoading";
import { Company } from "../../../../types/admin/CompanyTypes";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;

interface SupplementsTabProps {
	patient: Patient;
	company: Company;
}

const JOB_STATUS_LABEL: Record<JobStatus, string> = { pending: "Queued", processing: "Generating…", completed: "Done", failed: "Failed", cancelled: "Cancelled" };

const EMPTY_REGIMEN_TEMPLATE = (patientId: UserUuid): any => ({
	id: 0,
	uuid: "",
	patient_id: patientId,
	practitioner_id: "",
	name: "New Supplement Regimen",
	notes: "",
	summary: "",
	type: "manual",
	status: "draft",
	questionnaire_answers: null,
	regimen: { morning: [], lunch: [], dinner: [], night: [], other: [] },
	created_at: "",
	updated_at: "",
});

const SupplementsTab: React.FC<SupplementsTabProps> = ({ patient, company }) => {
	const user: User = getSessionUser();
	const { showSuccess, showError } = useNotifications();

	const [regimens, setRegimens] = useState<SupplementRegimen[]>([]);
	const [loading, setLoading] = useState(false);
	const [catalog, setCatalog] = useState<Product[]>([]);
	const [showQuestionnaire, setShowQuestionnaire] = useState(false);
	const [pollingJob, setPollingJob] = useState<{ uuid: string; status: JobStatus; regimenId?: number } | null>(null);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const [editingRegimen, setEditingRegimen] = useState<SupplementRegimen | null>(null);
	const [viewRegimen, setViewRegimen] = useState<SupplementRegimen | null>(null);

	useEffect(() => {
		loadRegimens();
		loadCatalog();
		return () => {
			if (pollingRef.current) clearInterval(pollingRef.current);
		};
	}, [patient.id]);

	const loadRegimens = async () => {
		if (!patient.id) return;
		setLoading(true);
		try {
			setRegimens(await listPatientRegimens(patient.id));
		} catch {
			showError("Failed to load supplement regimens");
		} finally {
			setLoading(false);
		}
	};

	const loadCatalog = async () => {
		try {
			const comp = await fetchClinicCompany();
			setCatalog(await getSupplementCatalog(comp.id));
		} catch {}
	};

	const handleQuestionnaireComplete = async (answers: SupplementQuestionnaireAnswers) => {
		setShowQuestionnaire(false);
		try {
			const job = await queueSupplementJob({ patient_id: patient.id, practitioner_id: user.id, patient_profile: patient as any, questionnaire_answers: answers });
			setPollingJob({ uuid: job.job_uuid, status: job.status as JobStatus });
			startPolling(job.job_uuid);
		} catch {
			showError("Failed to start supplement generation");
		}
	};

	const startPolling = (jobUuid: string) => {
		if (pollingRef.current) clearInterval(pollingRef.current);
		pollingRef.current = setInterval(async () => {
			try {
				const st = await getSupplementJobStatus(jobUuid);
				setPollingJob((prev) => (prev ? { ...prev, status: st.status, regimenId: st.supplement_regimen_id } : null));
				if (st.status === "completed") {
					clearInterval(pollingRef.current!);
					const updated = await listPatientRegimens(patient.id);
					setRegimens(updated);
					const nr = updated.find((r) => r.id === st.supplement_regimen_id);
					if (nr) setEditingRegimen(nr);
					setPollingJob(null);
					showSuccess("Supplement regimen generated! Review and activate below.");
				} else if (st.status === "failed") {
					clearInterval(pollingRef.current!);
					setPollingJob(null);
					showError(`Generation failed: ${st.error_message || "Unknown error"}`);
				}
			} catch {}
		}, 3000);
	};

	const handleCancelJob = async () => {
		if (!pollingJob) return;
		try {
			await cancelSupplementJob(pollingJob.uuid);
			if (pollingRef.current) clearInterval(pollingRef.current);
			setPollingJob(null);
		} catch {
			showError("Failed to cancel job");
		}
	};

	const handleArchive = (uuid: string, name: string) => {
		Modal.confirm({
			title: `Archive "${name}"?`,
			content: "Archived regimens are hidden but can be found by filtering.",
			okType: "danger",
			okText: "Archive",
			onOk: async () => {
				try {
					await archiveRegimen(uuid);
					await loadRegimens();
					showSuccess("Regimen archived");
				} catch {
					showError("Failed to archive");
				}
			},
		});
	};

	const handleDuplicate = async (uuid: string) => {
		try {
			await duplicateRegimen(uuid);
			await loadRegimens();
			showSuccess("Regimen duplicated");
		} catch {
			showError("Failed to duplicate");
		}
	};

	const columns: ColumnsType<SupplementRegimen> = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (name: string, r) => (
					<Text>{name}</Text>
			),
		},
		{ title: "Status", dataIndex: "status", key: "status", render: (s: string) => <Text>{s}</Text> },
		{ title: "Items", key: "items", render: (_: any, r) => <Badge count={Object.values(r.regimen || {}).flat().length} showZero style={{ backgroundColor: "var(--mg-text-secondary)" }} /> },
		{
			title: "Created",
			dataIndex: "created_at",
			key: "created_at",
			render: (d: string) => (d ? format(new Date(d), "dd MMM yyyy") : "—"),
			sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
			defaultSortOrder: "descend",
		},
		{
			title: "",
			key: "actions",
			fixed: "right" as const,
			render: (_: any, r) => (
				<Space size={4} onClick={(e) => e.stopPropagation()}>
					<Button size="small" title="View" icon={<EyeOutlined />} onClick={() => setViewRegimen(r)} />
					<Dropdown
						trigger={["click"]}
						menu={{
							items: [
								{ key: "duplicate", label: "Duplicate", icon: <CopyOutlined />, onClick: () => handleDuplicate(r.uuid) },
								{ key: "archive", label: "Archive", icon: <DeleteOutlined />, danger: true, onClick: () => handleArchive(r.uuid, r.name) },
							],
						}}
					>
						<Button size="small" icon={<EllipsisOutlined />} />
					</Dropdown>
				</Space>
			),
		},
	];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
				<Space>
					<Button icon={<RobotOutlined />} type="primary" onClick={() => setShowQuestionnaire(true)} disabled={!!pollingJob}>
						Generate with AI
					</Button>
					<Button icon={<PlusOutlined />} onClick={() => setEditingRegimen(EMPTY_REGIMEN_TEMPLATE(patient.id))}>
						Create Manually
					</Button>
				</Space>
			</div>

			{pollingJob && (
				<Alert
					type="info"
					showIcon
					icon={<InlineLoading minHeight={20} />}
					message={
						<Space>
							<Text>AI generating supplement regimen…</Text>
							<Tag>{JOB_STATUS_LABEL[pollingJob.status]}</Tag>
						</Space>
					}
					description="This usually takes 1–2 minutes. The regimen will open automatically when ready."
					action={
						<Button size="small" danger onClick={handleCancelJob}>
							Cancel
						</Button>
					}
				/>
			)}

			<DataTable
				columns={columns}
				dataSource={regimens.filter((r) => r.status !== "archived")}
				rowKey="id"
				loading={loading}
				emptyTitle="No supplement regimens yet"
				emptyDescription="Generate one with AI or create manually"
				emptyAction={{ label: "Generate with AI", onClick: () => setShowQuestionnaire(true) }}
				onRowClick={(r) => setEditingRegimen(r)}
			/>

			<SupplementQuestionnaire visible={showQuestionnaire} onComplete={handleQuestionnaireComplete} onCancel={() => setShowQuestionnaire(false)} />
			{editingRegimen !== null && <RegimenEditorDrawer regimen={editingRegimen as any} catalog={catalog} company={company} onClose={() => setEditingRegimen(null)} onSaved={loadRegimens} />}
			{viewRegimen !== null && <SupplementRegimenModal regimen={viewRegimen as any} visible={true} onClose={() => setViewRegimen(null)} />}
		</div>
	);
};

export default SupplementsTab;
